import { createQueryKeys } from '@lukemorales/query-key-factory';
import type { StoxlyClient } from '@stoxly/api-client';
import type { Body, Query } from '../op-types';

export function createSnaptradeQueries(client: StoxlyClient) {
  return createQueryKeys('snaptrade', {
    // ['snaptrade', 'connections']
    connections: {
      queryKey: null,
      queryFn: ({ signal }) => client.snaptradeListConnections({ signal }),
    },

    // ['snaptrade', 'registrationStatus']
    registrationStatus: {
      queryKey: null,
      queryFn: ({ signal }) => client.snaptradeGetRegistrationStatus({ signal }),
    },

    // ['snaptrade', 'openingLots', connectionId] — the fill-opening-lots flow: positions
    // whose opening lots the sync synthesized (pre-history holdings) or that still await
    // the broker's initial transaction history.
    openingLots: (connectionId: string) => ({
      queryKey: [connectionId],
      queryFn: ({ signal }) => client.snaptradeGetOpeningLots({ params: { connectionId }, signal }),
    }),
  });
}

export function createSnaptradeMutations(client: StoxlyClient) {
  return {
    handleCallback: {
      mutationFn: (body: Body<'snaptradeHandleCallback'>) => client.snaptradeHandleCallback(body),
    },
    getConnectionPortal: {
      mutationFn: (body: Body<'snaptradeGetConnectionPortal'>) =>
        client.snaptradeGetConnectionPortal(body),
    },
    disconnectBroker: {
      mutationFn: (id: string) => client.snaptradeDisconnectBroker(undefined, { params: { id } }),
    },
    refreshConnections: {
      mutationFn: () => client.snaptradeRefreshConnections(undefined),
    },
    syncAllConnections: {
      mutationFn: () => client.snaptradeSyncAllConnections(undefined),
    },
    syncConnection: {
      mutationFn: (connectionId: string) =>
        client.snaptradeSyncConnection(undefined, { params: { connectionId } }),
    },
    // Fill-opening-lots (step 4): the durable user override the next sync consumes
    // instead of the broker-average guess.
    upsertOpeningLot: {
      mutationFn: (input: { connectionId: string; body: Body<'snaptradeUpsertOpeningLot'> }) =>
        client.snaptradeUpsertOpeningLot(input.body, {
          params: { connectionId: input.connectionId },
        }),
    },
    deleteOpeningLot: {
      mutationFn: (input: { connectionId: string; query: Query<'snaptradeDeleteOpeningLot'> }) =>
        client.snaptradeDeleteOpeningLot(undefined, {
          params: { connectionId: input.connectionId },
          queries: input.query,
        }),
    },
  };
}
